import * as React from 'react';
import { useCallback } from 'react';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { DURATION, SnackbarProvider, useSnackbar } from 'baseui/snackbar';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { selectedNodeIdAtom, templateEdgesAtom, templateNodesAtom } from '../../../labelling-v2/atoms/skeleton-template-builder/template-atoms';
import { canRedoAtom, canUndoAtom, templateDispatchAtom } from '../../../labelling-v2/atoms/skeleton-template-builder/template-dispatch';
import { computeBoundingBoxAspectRatio, computeContainFitBox, nodesToKeypoints, normalizeToTightBoundingBox, toTemplateNodes } from '../../../labelling-v2/hooks/skeleton-template-builder/utils';
import { EVENTS, UNDOABLE_EVENTS } from '../../../labelling-v2/types/skeleton-template-builder.type';
import { CANVAS } from '../../constants/template-builder';
import type { SkeletonTemplateEdgeT, SkeletonTemplateNodeT } from '../../types/annotations.type';
import { validateSkeletonTemplate } from '../../utils/validate-skeleton-template';
import { TemplateCanvas } from './canvas/template-canvas';
import { TemplateCanvasContent } from './canvas/template-canvas-content';
import { TemplateHeader } from './header/template-header';
import { KeypointsSidePanel } from './keypoints-panel/keypoints-side-panel';
type SkeletonTemplateBuilderPropsT = {
  onSaveTemplate: (data: {
    nodes: SkeletonTemplateNodeT[];
    edges: SkeletonTemplateEdgeT[];
    aspectRatio: number;
  }) => void;
  onClose: () => void;
  initialNodes?: SkeletonTemplateNodeT[];
  initialEdges?: SkeletonTemplateEdgeT[];
  initialAspectRatio?: number;
};
const getRootStyles = (theme: Theme) => ({
  display: ('flex' as const),
  flexDirection: ('column' as const),
  height: '100%',
  width: '100%',
  overflow: ('hidden' as const),
  background: theme.colors.backgroundSecondary
});
const getBodyStyles = () => ({
  display: ('flex' as const),
  flex: 1,
  minHeight: '0',
  overflow: ('hidden' as const)
});
function TemplateBuilderContent({
  onSaveTemplate,
  onClose,
  initialNodes,
  initialEdges,
  initialAspectRatio
}: SkeletonTemplateBuilderPropsT) {
  const [css, theme] = useStyletron();
  const {
    enqueue
  } = useSnackbar();
  const dispatch = useSetAtom(templateDispatchAtom);
  const canUndo = useAtomValue(canUndoAtom);
  const canRedo = useAtomValue(canRedoAtom);
  const nodes = useAtomValue(templateNodesAtom);
  const edges = useAtomValue(templateEdgesAtom);
  const setNodes = useSetAtom(templateNodesAtom);
  const setEdges = useSetAtom(templateEdgesAtom);
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const handleCanvasReady = useCallback(([w, h]: [number, number]) => {
    if (!initialNodes?.length) return;
    const box = computeContainFitBox(w, h, initialAspectRatio ?? 1, CANVAS.PADDING);
    setNodes(toTemplateNodes(initialNodes).map(node => ({
      ...node,
      x: node.x * box.width + box.x,
      y: node.y * box.height + box.y
    })));
    setEdges(initialEdges ?? []);
  }, [initialNodes, initialEdges, initialAspectRatio, setNodes, setEdges]);
  const keypoints = nodesToKeypoints(nodes);
  const handleUndo = useCallback(() => dispatch({
    type: 'UNDO'
  }), [dispatch]);
  const handleRedo = useCallback(() => dispatch({
    type: 'REDO'
  }), [dispatch]);
  const handleClearCanvas = useCallback(() => {
    dispatch({
      type: UNDOABLE_EVENTS.CLEAR_CANVAS
    });
  }, [dispatch]);
  const handleDeleteKeypoint = useCallback((id: string) => {
    dispatch({
      type: UNDOABLE_EVENTS.DELETE_NODE,
      nodeId: id
    });
  }, [dispatch]);
  const handleColorChange = useCallback((nodeId: string, color: string) => {
    dispatch({
      type: EVENTS.CHANGE_NODE_COLOR,
      nodeId,
      color
    });
  }, [dispatch]);
  const handleSaveTemplate = useCallback(() => {
    const normalizedNodes = normalizeToTightBoundingBox(nodes);
    const aspectRatio = computeBoundingBoxAspectRatio(nodes);
    const error = validateSkeletonTemplate(normalizedNodes, edges, aspectRatio);
    if (error) {
      enqueue({
        message: error.message
      }, DURATION.short);
      return;
    }
    onSaveTemplate({
      nodes: normalizedNodes,
      edges,
      aspectRatio
    });
  }, [onSaveTemplate, nodes, edges, enqueue]);
  return <div className={css(getRootStyles(theme))}>
      <TemplateHeader onClearCanvas={handleClearCanvas} onSaveTemplate={handleSaveTemplate} onClose={onClose} />
      <div className={css(getBodyStyles())}>
        <TemplateCanvas onUndo={handleUndo} onRedo={handleRedo} canUndo={canUndo} canRedo={canRedo} onCanvasReady={handleCanvasReady}>
          <TemplateCanvasContent />
        </TemplateCanvas>
        <KeypointsSidePanel keypoints={keypoints} selectedKeypointId={selectedNodeId} onDeleteKeypoint={handleDeleteKeypoint} onColorChange={handleColorChange} />
      </div>
    </div>;
}
export function SkeletonTemplateBuilder(props: SkeletonTemplateBuilderPropsT) {
  return <Provider>
      <SnackbarProvider>
        <TemplateBuilderContent {...props} />
      </SnackbarProvider>
    </Provider>;
}